import { FC } from 'react';
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner';
import Button, { ButtonSizes } from '../../../../components/new-design/Button';
import useGetUser from '../../../../services/user/queries/useGetUser';
import Avatar from '../../../avatar/Avatar';
import { GoVerified } from 'react-icons/go';
import { LiaWalletSolid } from 'react-icons/lia';
import agentBadge from '../../../../assets/user-types/agent.svg';
import ambassadorBadge from '../../../../assets/user-types/ambassador.svg';
import Label from '../../../../components/Label';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import { getName } from 'country-list';
import ReactCountryFlag from 'react-country-flag';
import useGetWallets from '@/services/wallets/queries/useGetWallets';
import { IoClose } from 'react-icons/io5';
import { useDeleteWallet } from '@/services/wallets/queries/useDeleteWallet';
import useShowToast from '@/hooks/useShowToast';
import { useLocation, useNavigate } from 'react-router-dom';
import ROUTES from '../../../../routers/Routes';

interface PersonalPanelProps {
  setShowAddWalletModal: (value: boolean) => void;
}
const PersonalPanel: FC<PersonalPanelProps> = ({ setShowAddWalletModal }) => {
  const userId = useSelector((state: ReducerState) => state.auth.id);
  const navigate = useNavigate();
  const location = useLocation();

  const { isSuccess, isLoading, data } = useGetUser({
    userId: userId,
  });

  const {
    mutate: mutateDeleteWallet,
    isPending: isPendingDelete,
    isSuccess: isDeleteSuccess,
    isError: isErrorDelete,
  } = useDeleteWallet();

  const {
    data: walletsData,
    refetch,
    isRefetching: isRefetchingWallets,
    isLoading: isLoadingWallets,
  } = useGetWallets();

  useShowToast({
    isSuccess: isDeleteSuccess,
    isError: isErrorDelete,
    successMessage: 'Wallet deleted successfully',
    errorMessage: 'Error deleting a new wallet',
    callbackSuccess: refetch,
  });

  if (isLoading) {
    return (
      <div className="mx-auto my-auto w-full md:h-[380px]">
        <LoadingSpinner size={'20'} />
      </div>
    );
  }

  if (!isSuccess || !data) {
    return null;
  }

  const { data: userData } = data;

  const deleteWallet = (address: string) => {
    const id = walletsData?.data.find((wallet) => wallet.address === address)?.id;
    mutateDeleteWallet({ id });
  };

  const handleAvatar = () => {
    const currentPath = location.pathname;
    navigate(ROUTES.AVATURN, { replace: true, state: { from: currentPath } });
  };

  const hasAvatar = !!userData.avaturnUser?.avatarId;

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-4">
          <Avatar avatar2D className="h-25 w-25" userId={userData.id} />

          <div className="flex flex-col justify-center gap-1">
            <span className="flex items-center gap-1 text-xs font-semibold text-ph-primary-violet">
              <GoVerified size={16} /> Verfied
            </span>

            <h3 className="text-base text-ph-gray-white">{`${userData.firstName}` || `${userData.alias}`}</h3>

            <Button
              className="mt-2 py-2 hover:cursor-pointer"
              size={ButtonSizes.sm}
              type="button"
              onClick={handleAvatar}
            >
              {hasAvatar ? 'Change Avatar' : 'Create Avatar'}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {userData.isAgent && <img src={agentBadge} alt="Agent Badge" className="h-15 w-15" />}

          {userData.isAmbassador && <img src={ambassadorBadge} alt="Ambassador Badge" className="h-15 w-15" />}
        </div>
      </div>

      <div className="flex items-start justify-between gap-5 md:gap-8">
        <div className="flex w-3/12 flex-col gap-2 md:w-2/12">
          <Label semiBold>First Name</Label>
          <Label semiBold>Last Name</Label>
          <Label semiBold>Email</Label>
          <Label semiBold>Country</Label>
        </div>

        <div className="flex w-10/12 flex-col gap-2">
          <Label truncate>{userData.firstName || '-'}</Label>
          <Label truncate>{userData.lastName || '-'}</Label>
          <Label truncate>{userData.email || '-'}</Label>
          <Label truncate>
            <ReactCountryFlag countryCode={userData.countryCode || ''} /> {getName(userData.countryCode || '')}
          </Label>
        </div>
      </div>

      <div className="w-full border-t-[1px] border-ph-gray-baltic pt-5">
        <p className="text-xs font-semibold text-ph-gray-evening">My Wallets</p>
        {isPendingDelete || isRefetchingWallets || isLoadingWallets ? (
          <LoadingSpinner size="10" />
        ) : (
          walletsData?.data.map((wallet) => {
            return (
              <div className="flex items-center justify-between py-2">
                <span className="flex w-full items-center gap-1 overflow-hidden text-ellipsis text-base text-ph-gray-evening">
                  <LiaWalletSolid className="text-ph-primary-violet" />
                  {wallet.address}
                </span>

                <IoClose
                  size={20}
                  className="text-ph-gray-evening hover:cursor-pointer hover:text-ph-gray-silver"
                  onClick={() => deleteWallet(wallet.address)}
                />
              </div>
            );
          })
        )}

        <Button
          className="mt-2 py-2"
          size={ButtonSizes.sm}
          onClick={() => {
            setShowAddWalletModal(true);
          }}
        >
          Add Wallet
        </Button>
      </div>
    </div>
  );
};

export default PersonalPanel;
